import { Chip, Stack, SxProps, Tooltip } from '@mui/material'
import React from 'react'

import { isInfraAttackNode } from 'components/scenarios/utils/graph'
import { useTranslation } from 'hooks/use-translation'
import {
  ScenarioPreview,
  ScenarioPreviewMetadataKey,
  ScenarioPreviewSentinels
} from 'types/api/reliability-tests/preview'
import { GraphNode, InfraAttackNode } from 'types/api/scenarios/scenario'

export function getHasRandomZoneSentinel(step: InfraAttackNode) {
  // @ts-expect-error getting this type right is tricky b/c mixed camel and snake casing
  return step?.attack_configuration?.command?.infraCommandArgs?.traffic_impact_mapping?.multi_select_tags?.zone?.includes(
    ScenarioPreviewSentinels.RandomZone
  )
}

export function getHasRandomDnsSentinel(step: InfraAttackNode) {
  return (
    // @ts-expect-error getting this type right is tricky b/c mixed camel and snake casing
    step?.attack_configuration?.command?.infraCommandArgs?.ip_addresses ===
    ScenarioPreviewSentinels.RandomDNS
  )
}

export const PreviewChip: React.FC<{
  chipText: string
  tooltipText: string
}> = ({ chipText, tooltipText }) => {
  return (
    <Tooltip arrow={true} title={tooltipText}>
      <Chip
        label={chipText}
        size="small"
        sx={{
          color: 'white',
          backgroundColor: 'primary.light'
        }}
        variant="filled"
      />
    </Tooltip>
  )
}

export const PromotedScenarioPreviewChips: React.FC<{
  scenarioPreview?: ScenarioPreview
  step?: GraphNode
  sx?: SxProps
}> = ({ scenarioPreview, sx, step }) => {
  const { trans } = useTranslation()

  if (!scenarioPreview || !step) {
    return null
  }

  const { metadata } = scenarioPreview

  const previewMetadata = Boolean(metadata) && metadata[step.guid]
  const isInfra = isInfraAttackNode(step)

  if (!previewMetadata || !isInfra) {
    return null
  }

  /**
   * FYI - reliability-tests/preview is a private endpoint and this handling of it
   * is a proof-of-concept / in flux.
   * The consideration in the design phase of the ScenarioPreview data structure
   * was finding a balance of "top level" metadata and needing to modify the graph itself.
   * - 'Service' and 'Dependency' are indicated in the metadata.
   * - The 'Random Zone' tag is in the graph itself. (Other tags may be added in the future.)
   */

  const hasService = previewMetadata.includes(
    ScenarioPreviewMetadataKey.Service
  )
  const hasServiceDependency = previewMetadata.includes(
    ScenarioPreviewMetadataKey.Dependency
  )
  const hasRandomZone = getHasRandomZoneSentinel(step)
  const hasRandomDns = getHasRandomDnsSentinel(step)

  if (!(hasService || hasServiceDependency || hasRandomZone || hasRandomDns)) {
    return null
  }

  return (
    <Stack direction="row" gap={1} sx={sx}>
      {hasService && (
        <PreviewChip
          chipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.service.label'
          )}
          tooltipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.service.tooltip'
          )}
        />
      )}
      {hasServiceDependency && (
        <PreviewChip
          chipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.dependency.label'
          )}
          tooltipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.dependency.tooltip'
          )}
        />
      )}
      {hasRandomZone && (
        <PreviewChip
          chipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.random_zone.label'
          )}
          tooltipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.random_zone.tooltip'
          )}
        />
      )}
      {hasRandomDns && (
        <PreviewChip
          chipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.random_dns.label'
          )}
          tooltipText={trans(
            'test_suites.details.add_edit_drawer.test_preview.chip.random_dns.tooltip'
          )}
        />
      )}
    </Stack>
  )
}
